/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { QUERY_PARAM } from "../../constants/queryParams";
import {
  getCaseStudyData,
  getIndustryCategories,
  getTechnologyCategories,
  resetPagination,
} from "../../redux/slices/caseStudiesSlice";
import { getAuthTokenAPI } from "../../utils/oAuthApi";
import Categories from "../Categories";
import ClearAllButton from "../ClearAllButton";
import Skeleton from "../Skeleton";
import SearchInput from "../SearchInput";
import CaseStudyComponent from "./CaseStudyCardView";
import CaseStudyFetcher from "./CaseStudyFetch";

const CaseStudy = ({ props }) => {
  const dispatch = useDispatch();
  const url = new URL(window.location.href);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesToShow, setEntriesToShow] = useState(6);
  const [industriesCategoryId, setIndustriesCategoryId] = useState("");
  const [isClearAllEnabled, setIsClearAllEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [technologyCategoryId, setTechnologyCategoryId] = useState("");
  const isInitialLoad = useRef(true);
  const {
    isCaseStudyLoading,
    isTechnologyDataLoading,
    isIndustryDataLoading,
    caseStudyData,
    technologyCategoriesList,
    industriesCategoriesList,
    hasMoreData,
  } = useSelector((state) => state.caseStudy);

  useEffect(() => {
    if (!isCaseStudyLoading && !isTechnologyDataLoading && !isIndustryDataLoading) {
      setIsLoading(false);
      
    } else {
      setIsLoading(true);
    }
  }, [isCaseStudyLoading, isTechnologyDataLoading, isIndustryDataLoading,]);


  useEffect(() => {
    setIsLoading(true);
    const params = new URLSearchParams(url.search);
    const initialLoad = async () => {
      // Set all states at once without triggering multiple rerenders
      const newSearchValue = params.get(QUERY_PARAM.SEARCH_TERM) || "";
      const newTechnologyId =
        params.get(QUERY_PARAM.TECHNOLOGY_CATEGORY_ID) || "";
      const newIndustryId =
        params.get(QUERY_PARAM.INDUSTRIES_CATEGORY_ID) || "";

      setSearchValue(newSearchValue);
      setTechnologyCategoryId(newTechnologyId);
      setIndustriesCategoryId(newIndustryId);

      // Make single API call with the values from URL
      const authTokenObject = {
        client_id: props.clienId,
        client_secret: props.clientSecret,
        grant_type: props.grantType,
      };
      const token = await getAuthTokenAPI(authTokenObject);

      const caseStudyRequest = {
        token: token,
        searchValue: newSearchValue,
        technologyCategoryId: newTechnologyId,
        industriesCategoryId: newIndustryId,
        structureId: props.structureId,
        page: currentPage,
        pageSize: 7,
      };
      
      dispatch(getCaseStudyData(caseStudyRequest));
      setIsLoadingMore(false);
    };
    initialLoad();
  }, []);

  useEffect(() => {
    setIsLoading(true);
    if (isInitialLoad.current) {
      const params = new URLSearchParams(url.search);

      if (params.has(QUERY_PARAM.SEARCH_TERM)) {
        setSearchValue(params.get(QUERY_PARAM.SEARCH_TERM));
      }
      if (params.has(QUERY_PARAM.TECHNOLOGY_CATEGORY_ID)) {
        setTechnologyCategoryId(params.get(QUERY_PARAM.TECHNOLOGY_CATEGORY_ID));
      }
      if (params.has(QUERY_PARAM.INDUSTRIES_CATEGORY_ID)) {
        setIndustriesCategoryId(params.get(QUERY_PARAM.INDUSTRIES_CATEGORY_ID));
      }

      isInitialLoad.current = false;
      return;
    }

    fetchCaseStudies();
  }, [
    searchValue,
    technologyCategoryId,
    industriesCategoryId,
    currentPage,
  ]);

  useEffect(() => {
    dispatch(resetPagination());
    setCurrentPage(1);
  }, [technologyCategoryId, industriesCategoryId, searchValue, dispatch]);

  // Function to set or remove query parameter based on value
  const updateQueryParam = (param, value) => {
    if (value) {
      url.searchParams.set(param, value);
    } else {
      url.searchParams.delete(param);
    }
  };

  useEffect(() => {
    const fetchCategories = async () => {
      if (technologyCategoriesList.length === 0) {
        const authTokenObject = {
          client_id: props.clienId,
          client_secret: props.clientSecret,
          grant_type: props.grantType,
        };
        const token = await getAuthTokenAPI(authTokenObject);

        const technologyCategoriesRequest = {
          token: token,
          technologiesVocabularyId: props.technologiesVocabularyId,
        };
        dispatch(getTechnologyCategories(technologyCategoriesRequest));

        const industriesCategoriesRequest = {
          token: token,
          industryVocabularyId: props.industriesVocabularyId,
        };
        dispatch(getIndustryCategories(industriesCategoriesRequest));
      }
    };

    fetchCategories();
  }, [
    technologyCategoriesList,
    industriesCategoriesList,
  ]);

  useEffect(() => {
    setIsClearAllEnabled(
      technologyCategoryId !== "" ||
        industriesCategoryId !== "" ||
        searchValue !== ""
    );
  }, [technologyCategoryId, industriesCategoryId, searchValue]);

  const filteredTechnologyCategories = technologyCategoriesList.filter(
    (category) =>
      [
        506708, 98368, 98380, 508965, 98413, 98422, 511702, 514045, 408140,
      ].includes(Number(category.id))
  );

  const filteredIndustriesCategories = industriesCategoriesList.filter(
    (category) =>
      [
        98472, 98481, 98487, 98490, 98499, 98493, 98496, 455133, 98502, 98505,
        98517, 98520, 98526, 455007,
      ].includes(Number(category.id))
  );

  const fetchCaseStudies = async () => {
    const authTokenObject = {
      client_id: props.clienId,
      client_secret: props.clientSecret,
      grant_type: props.grantType,
    };

    const token = await getAuthTokenAPI(authTokenObject);
    const caseStudyRequest = {
      token: token,
      searchValue: searchValue,
      technologyCategoryId: technologyCategoryId,
      industriesCategoryId: industriesCategoryId,
      structureId: props.structureId,
      page: currentPage,
      pageSize: 7,
    };
    dispatch(getCaseStudyData(caseStudyRequest));
    setIsLoadingMore(false);
  };

  const handleCategorySelect = (categoryId, value, id) => {
    setIsLoading(true);
    setCurrentPage(1);
    setEntriesToShow(6);
    // Update state and query parameter based on id
    if (id === QUERY_PARAM.TECHNOLOGY_CATEGORY_ID) {
      setTechnologyCategoryId(value);
      updateQueryParam(QUERY_PARAM.TECHNOLOGY_CATEGORY_ID, value);
    } else if (id === QUERY_PARAM.INDUSTRIES_CATEGORY_ID) {
      setIndustriesCategoryId(value);
      updateQueryParam(QUERY_PARAM.INDUSTRIES_CATEGORY_ID, value);
    }

    // Update the browser's URL without reloading the page
    window.history.pushState({}, "", url);
  };

  const handleSearch = (term) => {
    setIsLoading(true);
    updateQueryParam(QUERY_PARAM.SEARCH_TERM, term);
    window.history.pushState({}, "", url);
    setSearchValue(term);
    dispatch(resetPagination());
    setCurrentPage(1);
  };

  const handleClearAll = () => {
    url.search = ""; // Remove all query parameters
    window.history.pushState({}, "", url); // Update the URL in the browser
    if (isClearAllEnabled) {
      setTechnologyCategoryId("");
      setIndustriesCategoryId("");
      setSearchValue("");
      dispatch(resetPagination());
      setCurrentPage(1);
      setEntriesToShow(6);
      setIsLoading(true);
    }
  };

  const handleLoadMore = () => {
    if (!isLoadingMore && hasMoreData) {
      setIsLoadingMore(true);
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  return (
    <>
      <div className="st-blog-list-section st-content-lg-wrap">
        <div className="container">
          <div className="st-blog-header">
            <div className="row align-items-center mx-0">
              <div className="col-lg-5 col-12 px-0">
                <div className="row st-categories-group d-flex align-items-center mx-0">
                  <div className="col-sm-6 col-12 px-0 pr-sm-1 mb-sm-0 mb-2">
                    <div className="st-category-select-wrap">
                      <Categories
                        categories={filteredTechnologyCategories}
                        id={"technology"}
                        name={"Technology"}
                        icon={"search-by-technology"}
                        onSelectCategory={handleCategorySelect}
                        selectedCategory={technologyCategoryId}
                      />
                    </div>
                  </div>
                  <div className="col-sm-6 col-12 px-0 pl-sm-1">
                    <div className="st-category-select-wrap">
                      <Categories
                        categories={filteredIndustriesCategories}
                        id={"industry"}
                        name={"Industries"}
                        icon={"search-by-industries"}
                        onSelectCategory={handleCategorySelect}
                        selectedCategory={industriesCategoryId}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-7 col-12 px-0 pl-lg-2">
                <div className="st-search-categories-wrapper d-flex align-items-center">
                  <SearchInput onSearch={handleSearch} onReset={searchValue} />
                  <ClearAllButton
                    onClearAll={handleClearAll}
                    clearAllButtonClass={
                      isClearAllEnabled
                        ? "st-clear-all-btn flex-shrink-0"
                        : "st-clear-all-btn-disabled flex-shrink-0"
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isLoading && caseStudyData.length < 7 ? (
        <Skeleton />
      ) : (
        <>
          <CaseStudyFetcher onDataFetched={() => {}} />
          <CaseStudyComponent
            isLoading={isLoading}
            entries={caseStudyData}
            handleLoadMore={handleLoadMore}
            hasMoreData={hasMoreData}
            displayedEntriesCount={caseStudyData.length}
            onClearAll={handleClearAll}
            setEntriesToShow={setEntriesToShow}
            entriesToShow={entriesToShow}
            handleClearAll={handleClearAll}
          />
          {isLoadingMore && <skeleton />}
        </>
      )}
    </>
  );
};

export default CaseStudy;
